import type { RefObject } from "react";
import { useCallback, useEffect, useLayoutEffect, useMemo } from "react";
import { addCalendarDays } from "@common/helpers/date";
import { useLatestRef } from "@design-system/hooks/useLatestRef";
import type {
    DensityChartDay,
    DensityChartSeries,
    DensityChartWindow,
} from "./DensityChart.lib";
import { clampToHistory } from "./DensityChart.lib";
import { buildBars } from "./densityBars.lib";
import type {
    DensityChartBucket,
    DensityChartBucketUnit,
} from "./densityBuckets.lib";
import {
    barStartDate,
    buildBuckets,
    countBarsBefore,
} from "./densityBuckets.lib";
import { BAR_PITCH_PX } from "./densityViewport.lib";
import { useDensitySwap } from "./useDensitySwap";
import { useDensityViewport } from "./useDensityViewport";
import { windowToSpan } from "./windowSpan.lib";

interface UseDensityStripParams {
    viewportRef: RefObject<HTMLDivElement | null>;
    /** The chart, whose window outline and axis ticks ease when the unit changes. */
    root: HTMLElement | null;
    startDate: string;
    endDate: string;
    /** The closest unit wanted; the bars show a coarser one while the window doesn't fit it. */
    closestUnit: DensityChartBucketUnit;
    window?: DensityChartWindow;
    days: DensityChartDay[];
    /** The runs of days whose values have loaded; every day when omitted. */
    loadedDays?: readonly DensityChartWindow[];
    /** The runs of days whose values failed to load. */
    failedDays?: readonly DensityChartWindow[];
    /** The runs of days showing values kept from before. */
    staleDays?: readonly DensityChartWindow[];
    series: DensityChartSeries[];
    /** Called when the bars move, e.g. to close a card pinned to one. */
    onPan: () => void;
    onWindowChange?: (window: DensityChartWindow) => void;
    onWindowPreview?: (window: DensityChartWindow | null) => void;
    /** Called with the unit the bars show whenever it or `closestUnit` changes. */
    onShownUnitChange?: (unit: DensityChartBucketUnit) => void;
    /** Called with the days the drawn bars cover whenever they change. */
    onDrawnDaysChange?: (drawnDays: DensityChartWindow) => void;
}

/** What a window drag needs from the strip of bars it pans. */
export interface WindowDragStrip {
    /** The bars' unit; a window dragged in another unit is dropped. */
    unit: DensityChartBucketUnit;
    /** The first drawn bar from the epoch, and the strip's offset in bars; both change while a drag pans. */
    firstBar: number;
    offset: number;
    /** Where the bars in view start and end, in bars from `firstBar`. */
    visible: { start: number; end: number };
    /** The visible bars, whose edges pan the strip. */
    edgeRef: RefObject<HTMLElement | null>;
    /** Pans by px, negative for earlier. */
    panByPx: (px: number) => void;
    /** Keeps the drawn bars drawn from press to release, so the window's far edge stays on the strip. */
    holdBars: () => void;
    releaseBars: () => void;
}

const isWithin = (bucket: DensityChartBucket, days: DensityChartWindow) =>
    days.start <= bucket.start && bucket.end <= days.end;

/**
 * The drawn bars, today's ending today, in the unit the window fits, with
 * their segments, which have loaded, failed or are stale, and where they and the window
 * sit in the viewport. Bars that end before `startDate` are the zone before the
 * start. Windows start no earlier than `startDate`, even partway into its
 * bar, and picked ones keep the bars
 * where they are.
 */
export function useDensityStrip({
    viewportRef,
    root,
    startDate,
    endDate,
    closestUnit,
    window,
    days,
    loadedDays,
    failedDays,
    staleDays,
    series,
    onPan,
    onWindowChange,
    onWindowPreview,
    onShownUnitChange,
    onDrawnDaysChange,
}: UseDensityStripParams) {
    const {
        unit,
        viewport,
        width,
        isEasing,
        pan,
        panByPx,
        holdBars,
        releaseBars,
        markPickedWindow,
    } = useDensityViewport({
        viewportRef,
        root,
        startDate,
        endDate,
        closestUnit,
        window,
        onPan,
    });
    useDensitySwap({
        root,
        unit,
        offset: viewport.offset,
        width,
        startDate,
        endDate,
        window,
    });
    const onShownUnitChangeRef = useLatestRef(onShownUnitChange);
    useLayoutEffect(
        () => onShownUnitChangeRef.current?.(unit),
        [onShownUnitChangeRef, unit, closestUnit],
    );
    const { firstBar, barCount, endBar, visibleStart, visibleBars } = viewport;
    const drawnStart = barStartDate({ index: firstBar, unit });
    const dragStrip: WindowDragStrip = {
        unit,
        firstBar,
        offset: viewport.offset,
        visible: { start: visibleStart, end: visibleStart + visibleBars },
        edgeRef: viewportRef,
        panByPx,
        holdBars,
        releaseBars,
    };
    const drawnEnd =
        barCount >= endBar
            ? endDate
            : addCalendarDays(
                  barStartDate({ index: firstBar + barCount, unit }),
                  -1,
              );
    const buckets = useMemo(
        () => buildBuckets({ startDate: drawnStart, endDate: drawnEnd, unit }),
        [drawnStart, drawnEnd, unit],
    );
    const zoneCount = useMemo(
        () => countBarsBefore(buckets, startDate),
        [buckets, startDate],
    );
    const segments = useMemo(
        () => buildBars({ buckets, unit, days, series }),
        [buckets, unit, days, series],
    );
    const staleBucketStarts = useMemo(
        () =>
            new Set(
                buckets.flatMap((bucket) =>
                    staleDays?.some((run) => isWithin(bucket, run))
                        ? [bucket.start]
                        : [],
                ),
            ),
        [buckets, staleDays],
    );
    const busyBucketStarts = useMemo(
        () => new Set(segments.map(({ bucket }) => bucket)),
        [segments],
    );
    const dayByDate = useMemo(
        () => new Map(days.map((day) => [day.date, day])),
        [days],
    );
    const isBarWithin = (
        index: number,
        runs: readonly DensityChartWindow[],
    ) => {
        const bucket = buckets[index];
        return !!bucket && runs.some((run) => isWithin(bucket, run));
    };
    const isBarLoaded = (index: number) =>
        !loadedDays || isBarWithin(index, loadedDays);
    const isBarFailed = (index: number) => isBarWithin(index, failedDays ?? []);
    const minBar = useMemo(
        () => windowToSpan({ start: startDate, end: startDate }, buckets).start,
        [buckets, startDate],
    );
    const windowSpan = useMemo(
        () =>
            window && windowToSpan(clampToHistory(window, startDate), buckets),
        [window, buckets, startDate],
    );
    const drawnDays = useMemo(
        () => clampToHistory({ start: drawnStart, end: drawnEnd }, startDate),
        [drawnStart, drawnEnd, startDate],
    );
    useEffect(() => {
        onDrawnDaysChange?.(drawnDays);
    }, [drawnDays, onDrawnDaysChange]);
    const keepPicked = useCallback(
        (picked: DensityChartWindow) => {
            const clamped = clampToHistory(picked, startDate);
            markPickedWindow(clamped);
            return clamped;
        },
        [markPickedWindow, startDate],
    );
    const pickWindow = useCallback(
        (picked: DensityChartWindow) => onWindowChange?.(keepPicked(picked)),
        [keepPicked, onWindowChange],
    );
    const previewWindow = useCallback(
        (preview: DensityChartWindow | null) =>
            onWindowPreview?.(preview && clampToHistory(preview, startDate)),
        [onWindowPreview, startDate],
    );
    const visibleStartPx = visibleStart * BAR_PITCH_PX;
    return {
        unit,
        viewport,
        isEasing,
        pan,
        dragStrip,
        /** The bars in view, in px from the first drawn bar's left. */
        visible: { start: visibleStartPx, end: visibleStartPx + width },
        buckets,
        zoneCount,
        /** The earliest a window may start: the history start, partway into its bar at Week or Month. */
        minBar,
        segments,
        staleBucketStarts,
        busyBucketStarts,
        dayByDate,
        isBarLoaded,
        isBarFailed,
        windowSpan,
        keepPicked,
        pickWindow,
        previewWindow,
    };
}
